import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Cuentas por contrato (ACUERDOS, secciones A a F). Todas las rutas van bajo /api/perfil-bancario del núcleo.
 * Leer pide config.perfil_bancario.read; guardar, volver y tocar el catálogo de etiquetas, config.cuentas_contrato.cambiar.
 * Los errores llegan tal cual (HttpErrorResponse): 422 con `bloqueos`, 409 con `choques` o `modificados`.
 */
@Injectable({ providedIn: 'root' })
export class CuentasContratoService {
  private baseUrl: string;

  constructor(private httpClient: HttpClient) {
    this.baseUrl = `${environment.BASE_LA}/api/perfil-bancario`;
  }

  /** Opciones de pago con sus cuentas MT y CT, cobros fijos, bloqueos sobre lo guardado y el corte. */
  getCuentasContrato = async (): Promise<any> => {
    return lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/cuentas-contrato`));
  };

  /** Bitácora paginada, lo más reciente primero. */
  getCambios = async (pagina = 1, limite = 10): Promise<any> => {
    return lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/cuentas-contrato/cambios?pagina=${pagina}&limite=${limite}`));
  };

  /** Guarda el borrador completo con su motivo (mínimo 10 caracteres). `nuevas` (clones de MT como CT) solo viaja si hay. */
  guardar = async (cambios: { opciones: any[]; cuentas: any[]; nuevas?: any[] }, motivo: string): Promise<any> => {
    return lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/cuentas-contrato/guardar`, { cambios, motivo }));
  };

  /** Deja todo como estaba antes de un cambio de la bitácora. */
  volver = async (id: string, motivo?: string): Promise<any> => {
    return lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/cuentas-contrato/cambios/${encodeURIComponent(id)}/volver`, motivo ? { motivo } : {}));
  };

  /** Catálogo de etiquetas; con `prefijo` sirve para autocompletar (sin tildes ni mayúsculas). */
  getEtiquetas = async (prefijo = '', limite = 50): Promise<any> => {
    const params: string[] = [`limite=${limite}`];
    if (prefijo) params.push(`prefijo=${encodeURIComponent(prefijo)}`);
    return lastValueFrom(this.httpClient.get<any>(`${this.baseUrl}/etiquetas?${params.join('&')}`));
  };

  /** Crea una etiqueta. Si ya existía con otras mayúsculas o tildes, devuelve la que existe. */
  crearEtiqueta = async (nombre: string, color?: string): Promise<any> => {
    return lastValueFrom(this.httpClient.post<any>(`${this.baseUrl}/etiquetas`, color ? { nombre, color } : { nombre }));
  };

  cambiarColorEtiqueta = async (id: string, color: string): Promise<any> => {
    return lastValueFrom(this.httpClient.put<any>(`${this.baseUrl}/etiquetas/${encodeURIComponent(id)}`, { color }));
  };
}
